import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'link-text-area',
  standalone: false,
  templateUrl: './link-text-area.component.html',
  styleUrls: ['./link-text-area.component.css']
})
export class LinkTextAreaComponent implements OnChanges, OnDestroy {
  @ViewChild('editableContent', { static: false }) editableContentRef: ElementRef;
  @ViewChild('labelForEditableContent', { static: false }) labelForEditableContent: ElementRef;
  @Input() value: any;
  @Input() labelText: string = '';
  @Output() changedTextEvent = new EventEmitter<string>();

  formattedText: SafeHtml;
  private savedSelection: Range | null = null;
  private selectionRestoreHandle: ReturnType<typeof setTimeout> | null = null;
  private focusOutHandle: ReturnType<typeof setTimeout> | null = null;
  backspace: boolean = false;
  startOffSet: number = 0;
  contentEditable: boolean = true;
  isFocused: boolean = false;
  plainTextValue: string = '';
  regex = /(https?|ftp):\/\/[^\s]+|www\.[^\s]+/gi;

  constructor(
    private sanitizer: DomSanitizer,
    private hostRef: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef
  ) {
    this.formattedText = '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      const enteredText = changes.value.currentValue || '';
      this.plainTextValue = enteredText;
      if (changes.value.currentValue === '') {
        this.formattedText = '';
        return;
      }
      const sanitizedText = enteredText.replace(
        this.regex,
        match =>
          '<a href=' +
          match +
          ' target="_blank" style="cursor: pointer;text-decoration: underline; color:#0b4c8c">' +
          match +
          '</a>'
      );
      this.formattedText = this.sanitizer.bypassSecurityTrustHtml(sanitizedText);
    }
  }

  ngOnDestroy(): void {
    this.clearSelectionRestore();
    if (this.focusOutHandle != null) {
      clearTimeout(this.focusOutHandle);
      this.focusOutHandle = null;
    }
  }

  onPaste(event: ClipboardEvent) {
    this.saveSelection();
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const pastedText = clipboardData.getData('text');
    this.startOffSet += pastedText.length;
  }

  onCut(_event: ClipboardEvent) {
    this.saveSelection();
  }

  onFocusIn() {
    if (this.focusOutHandle != null) {
      clearTimeout(this.focusOutHandle);
      this.focusOutHandle = null;
    }
    this.isFocused = true;
    this.cdr.markForCheck();
  }

  /**
   * focusout fires for mouse and keyboard. relatedTarget is often null when
   * clicking non-focusable UI, so confirm with activeElement on the next tick.
   * Pending restoreSelection timers must be cancelled so they cannot re-focus.
   */
  onFocusOut(_event: FocusEvent) {
    if (this.focusOutHandle != null) {
      clearTimeout(this.focusOutHandle);
    }
    this.focusOutHandle = setTimeout(() => {
      this.focusOutHandle = null;
      const host = this.hostRef.nativeElement;
      const active = document.activeElement;
      if (active && host.contains(active)) {
        return;
      }
      this.clearSelectionRestore();
      this.isFocused = false;
      this.plainTextValue =
        this.editableContentRef && this.editableContentRef.nativeElement
          ? this.editableContentRef.nativeElement.innerText
          : this.plainTextValue || '';
      this.cdr.markForCheck();
    }, 0);
  }

  handleInput(_event: Event) {
    const enteredText = this.editableContentRef.nativeElement.innerText;
    this.plainTextValue = enteredText;
    const sanitizedText = enteredText.replace(
      this.regex,
      '<a href="$&" target="_blank" style="cursor: pointer;text-decoration: underline; color:#0b4c8c">$&</a>'
    );
    this.formattedText = this.sanitizer.bypassSecurityTrustHtml(sanitizedText);
    this.changedText();
    this.scheduleRestoreSelection();
  }

  convertTextToLinkedText(originalText) {
    const sanitizedText = originalText.replace(
      this.regex,
      '<a href="$&" target="_blank" style="cursor: pointer;text-decoration: underline; color:#0b4c8c">$&</a>'
    );
    return sanitizedText;
  }

  handleLinkClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.tagName === 'A') {
      event.preventDefault();
      const url = (target as HTMLAnchorElement).getAttribute('href');
      if (!(url.startsWith('http://') || url.startsWith('https://') || url.startsWith('ftp://'))) {
        window.open('//' + url, '_blank');
      } else {
        window.open(url, '_blank');
      }
    }
  }

  handleKeyDown(event: KeyboardEvent) {
    this.backspace = event.key == 'Backspace';
    this.saveSelection();

    if (
      ((event.key === 'v' || event.key === 'V') && (event.ctrlKey || event.metaKey)) ||
      ((event.key === 'x' || event.key === 'X') && (event.ctrlKey || event.metaKey))
    ) {
      return;
    }

    if (event.key === 'Enter') {
      const position = this.getCursorOffsetRelativeToDiv();
      const enteredText = this.editableContentRef.nativeElement.innerText;
      const sanitizedText = this.insertCarrierReturn(enteredText, position);
      this.formattedText = this.sanitizer.bypassSecurityTrustHtml(sanitizedText);
      this.startOffSet++;
      this.scheduleRestoreSelection(() => this.handleInput(null));
      return;
    }

    if (this.backspace) {
      this.startOffSet--;
    } else {
      this.startOffSet++;
    }
    if (event.key === 'Delete') {
      this.startOffSet--;
    }
  }

  private saveSelection() {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      this.savedSelection = { ...selection.getRangeAt(0) } as Range;
      this.startOffSet = selection.getRangeAt(0).startOffset;
    }

    if (selection.anchorNode && selection.anchorNode.nodeType === Node.TEXT_NODE) {
      this.startOffSet = this.getCursorOffsetRelativeToDiv();
    }
  }

  private clearSelectionRestore() {
    if (this.selectionRestoreHandle != null) {
      clearTimeout(this.selectionRestoreHandle);
      this.selectionRestoreHandle = null;
    }
  }

  private scheduleRestoreSelection(after?: () => void) {
    this.clearSelectionRestore();
    this.selectionRestoreHandle = setTimeout(() => {
      this.selectionRestoreHandle = null;
      if (!this.isFocused) {
        return;
      }
      this.restoreSelection();
      if (after) {
        after();
      }
    }, 1);
  }

  private restoreSelection() {
    try {
      if (!this.isFocused) {
        return;
      }

      const editableDiv = this.editableContentRef.nativeElement;
      var positionAtNode = this.startOffSet;
      var cumulativeLenghts = 0;
      var currentNode;
      const childNodesArray = Array.from(editableDiv.childNodes);

      childNodesArray.every(n => {
        var node = n as Text | Element;
        cumulativeLenghts += node.textContent.length;
        if (this.startOffSet > cumulativeLenghts) {
          positionAtNode = this.startOffSet - cumulativeLenghts;
          return true;
        } else {
          return false;
        }
      });
      cumulativeLenghts = 0;
      childNodesArray.every(n => {
        var node = n as Text | Element;
        cumulativeLenghts += node.textContent.length;
        if (this.startOffSet <= cumulativeLenghts) {
          currentNode = node;
          return false;
        }

        return true;
      });

      editableDiv.contentEditable = 'true';
      editableDiv.focus();
      const lastTextNode = currentNode;
      const range = document.createRange();
      if (lastTextNode && lastTextNode.textContent) {
        if (lastTextNode.nodeName == 'A') {
          range.setStart(lastTextNode.childNodes[0], positionAtNode);
          range.setEnd(lastTextNode.childNodes[0], positionAtNode);
        } else {
          range.setStart(lastTextNode, positionAtNode);
          range.setEnd(lastTextNode, positionAtNode);
        }
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    } catch (e) {
      console.log(e);
    }
  }

  getCursorOffsetRelativeToDiv() {
    const editableDiv = this.editableContentRef.nativeElement;

    const selection = window.getSelection();
    const range = selection.getRangeAt(0);

    const cursorRange = document.createRange();
    cursorRange.selectNodeContents(editableDiv);
    cursorRange.setEnd(range.startContainer, range.startOffset);

    const cursorOffset = cursorRange.toString().length;

    cursorRange.detach();
    range.detach();

    return cursorOffset;
  }

  insertCarrierReturn(originalString, position) {
    var character = '\n';
    if (position < 0 || position > originalString.length) {
      throw new Error('Invalid position');
    }

    if (position == originalString.length) {
      character += '\n';
    }

    const part1 = originalString.substring(0, position);
    const part2 = originalString.substring(position);
    const modifiedString = part1 + character + part2;

    return modifiedString;
  }

  setCursor() {
    this.editableContentRef.nativeElement.focus();
  }

  handleTextAreaClick(_event) {
    // Focus event drives isFocused; only move caret into the editable.
    this.setCursor();
  }

  get hasValue(): boolean {
    const t = (this.plainTextValue || '').replace(/\n/g, '').trim();
    return t.length > 0;
  }

  changedText() {
    this.changedTextEvent.emit(this.editableContentRef.nativeElement.innerText);
  }

  isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }
}
